<template>
  <Card class="mb-3 ga-3">
    <div>
      <div class="cardTitle">{{ $t('Требования к участнику ({_1})', { '_1': supplierRequirementValues.length }) }}</div>
      <div
        v-if="isEdit"
        class="card-extra"
      >{{ $t('Заполните требования к участнику') }}</div>
    </div>
    <div class="d-flex flex-column ga-3 requirements-contrainer">
      <div
        v-for="(requirementValue, index) in supplierRequirementValues"
        :key="index"
        class="d-flex flex-column ga-3"
      >
        <div v-if="(isEdit && requirementValue.type !== SUPPLIER_REQUIREMENT_TYPE.BOOL) || isView">
          <div class="cardTitle">{{ requirementValue.name }}</div>
          <div
            v-if="requirementValue.description && isEdit"
            class="card-extra"
          >
            {{ requirementValue.description }}
          </div>
        </div>

        <div v-if="requirementValue.type === SUPPLIER_REQUIREMENT_TYPE.TEXT">
          <Input
            label="Текст"
            :class="{
              'required': requirementValue.isRequired,
              'disabled': isView
            }"
            :rules="[].concat(requirementValue.isRequired ? validation.required() : [])"
            maxlength="255"
            hide-details="auto"
            v-model="requirementModels[index].valueText"
            :disabled="isView"
          />
        </div>
        <div v-else-if="requirementValue.type === SUPPLIER_REQUIREMENT_TYPE.BOOL">
          <template v-if="isEdit">
            <v-checkbox
              :label="requirementValue.name"
              :class="{ 'disabled': isView }"
              :disabled="isView"
              class="requirement-value-bool"
              hide-details="auto"
              v-model="requirementValue.valueBool"
            >
              <template v-slot:label="{ label }">
                <span>
                  {{ label }}
                  <span v-if="requirementValue.isRequired" class="required-star">*</span>
                </span>
              </template>
            </v-checkbox>
            <div
              v-if="requirementValue.description && isEdit"
              class="card-extra"
            >
              {{ requirementValue.description }}
            </div>
          </template>
          <Input
            v-else
            label="Да/Нет"
            :class="{
              'required': requirementValue.isRequired,
              'disabled': isView
            }"
            :disabled="isView"
            v-model="requirementModels[index].valueBool"
            hide-details="auto"
          />
        </div>
        <div v-else-if="requirementValue.type === SUPPLIER_REQUIREMENT_TYPE.NUMBER">
          <Input
            label="Число"
            :class="{
              'required': requirementValue.isRequired,
              'disabled': isView
            }"
            :rules="[].concat(requirementValue.isRequired ? validation.required() : [])"
            maxlength="255"
            hide-details="auto"
            only-number
            v-model="requirementModels[index].valueNumber"
            :disabled="isView"
          />
        </div>
        <div v-else-if="requirementValue.type === SUPPLIER_REQUIREMENT_TYPE.DATE">
          <DateTimePicker
            v-if="isEdit"
            :lang="lang"
            :required="requirementValue.isRequired"
            :class="{
              'required': requirementValue.isRequired,
              'disabled': isView
            }"
            hide-time
            label="Дата"
            :rules="[].concat(requirementValue.isRequired ? validation.required() : [])"
            :deafaultNow="true"
            v-model="requirementValue.valueDate"
            :error="errors['requirementValueDate' + index] ?? ''"
            @input="errors['requirementValueDate' + index] = null"
            @update:model-value="(newValue) => $emit('date-change', newValue, 'requirementValueDate' + index, requirementValue.isRequired)"
          />
          <Input
            v-else
            label="Дата"
            :class="{
              'required': requirementValue.isRequired,
              'disabled': isView
            }"
            :disabled="isView"
            v-model="requirementModels[index].valueDate"
            hide-details="auto"
          />
        </div>
        <div v-else-if="requirementValue.type === SUPPLIER_REQUIREMENT_TYPE.LIST">
          <SelectInput
            :class="{
              'required': requirementValue.isRequired,
              'disabled': isView
            }"
            label="Список"
            item-title="value"
            item-value="id"
            v-model="requirementValue.valueList"
            :items="requirementValue.list"
            hide-details="auto"
            :rules="[].concat(requirementValue.isRequired ? validation.required() : [])"
          />
        </div>
        <div v-else-if="requirementValue.type === SUPPLIER_REQUIREMENT_TYPE.FILE">
          <FileInput
            v-if="isEdit || (isView && supplierRequirementDocuments[requirementValue.supplierRequirementId]?.length)"
            class="mx-1"
            title="Файл"
            :without-title="isView"
            :no-card="isView"
            :is-edit="isEdit"
            title-class="cardSubTitle"
            :class="{
              'required': requirementValue.isRequired,
              'disabled': isView
            }"
            :required="requirementValue.isRequired"
            :upload-extensions="allowedUploadExtensions"
            :maxCountFiles="1"
            :documents="supplierRequirementDocuments[requirementValue.supplierRequirementId]"
            :upload-file="(file) => $emit('upload', file, requirementValue)"
            :delete-file="(document) => $emit('delete', document, requirementValue)"
            :download-file="(documentId) => $emit('download', documentId, requirementValue)"
            :file-rules="rules.checkFile"
            :on-error="(err) => $emit('error', err)"
            :format-file-info="[
              (file) => formatMinuteTimeNoTZ(file.createdAt),
              (file) => file.fileSizeFmt.toLowerCase(),
            ]"
            without-sub-title
          />
          <Input
            v-else
            label="Файл"
            :class="{
              required: requirementValue.isRequired,
              disabled: isView
            }"
            :disabled="isView"
            :model-value="$t('Не приложен')"
            hide-details="auto"
          />
        </div>

        <v-divider
          v-if="index + 1 !== supplierRequirementValues.length && isEdit"
        />
      </div>
    </div>
  </Card>
</template>

<script>
import Config from '@/config';
import Input from "@intec/vue-components/form/Input.vue";
import FileInput from '@intec/vue-components/form/FileInput.vue';
import SelectInput from '@intec/vue-components/form/SelectInput.vue';
import Card from "@intec/vue-components/form/Card.vue";
import DateTimePicker from '@intec/vue-components/form/DateTimePicker.vue';
import validation from "@/components/validation";
import { checkFileValidation } from '@/lib/file';
import { SUPPLIER_REQUIREMENT_TYPE } from '@/lib/supplierRequirement';
import { computed, reactive } from "vue";
import {
  formatMinuteTimeNoTZ,
  formatDate,
} from '@/filters';

export default {
  name: 'SupplierRequirementValues',
  components: {
    Card,
    DateTimePicker,
    FileInput,
    Input,
    SelectInput,
  },
  props: {
    supplierRequirementValues: { type: Array, required: true },
    supplierRequirementDocuments: { type: Object, required: true },
    mode: { type: String, required: true },
    errors: { type: Object, required: true }
  },
  emits: ['upload', 'delete', 'download', 'error', 'date-change'],
  data() {
    return {
      allowedUploadExtensions: (Config.get('config').allowedUploadExtensions || []).join(','),
      rules: {
        checkFile: [checkFileValidation],
      },
      lang: Config.getUser().getLang(),
      SUPPLIER_REQUIREMENT_TYPE,
      validation,
      formatMinuteTimeNoTZ,
    }
  },
  computed: {
    isView() {
      return this.mode === 'VIEW';
    },
    isEdit() {
      return this.mode === 'EDIT';
    },
    /**
     * Отображение значений требований к участнику.
     */
    requirementModels() {
      return this.supplierRequirementValues.map(req => reactive({
        valueText: computed({
          get: () => this.isView ? req.valueText || this.$t('Не указано') : req.valueText,
          set: val => req.valueText = val
        }),
        valueNumber: computed({
          get: () => this.isView ? req.valueNumber || this.$t('Не указано') : req.valueNumber,
          set: val => req.valueNumber = val
        }),
        valueDate: computed({
          get: () => this.isView
            ? (req.valueDate ? formatDate(req.valueDate) : this.$t('Не указано'))
            : req.valueDate,
          set: val => req.valueDate = val
        }),
        valueBool: computed({
          get: () => this.isView
            ? this.$t((req.valueBool ? 'Да' : 'Нет'))
            : req.valueBool,
          set: val => req.valueBool = val
        }),
      }));
    },
  },
}
</script>

<style scoped lang="scss">
$color-black: #000;
$color-error: #a01111;
$color-gray: #949494;
$color-gray-2: #868686;

.cardTitle sup {
  color: $color-error;
}

.card-extra {
  font-size: 14px;
  color: $color-gray;
}

:deep(.v-select > .v-input__control) {
  height: 58.5px;
}

.required :deep(.v-label::after) {
  content: ' *';
  color: rgba(var(--error-color));
}

.required-star {
  margin-left: 4px;
  color: rgba(var(--error-color)) !important;
  font-size: 1rem !important;
}

:deep(.not-required .v-label::after) {
  content: '';
  color: transparent;
}

:deep(.v-input__details) {
  min-height: 10px;

  .v-messages > .v-messages__message {
    font-size: 12px;
    font-weight: 400;
    padding: 0 12px;
  }
}

.requirements-contrainer {
  max-height: 850px;
  overflow-y: auto;

  .date-time-picker {
    width: 100% !important;
  }

  .requirement-value-bool {
    :deep(.v-label) {
      opacity: 1;
      span {
        color: var(--black-color);
        font-size: 18px;
        font-style: normal;
        font-weight: 600;
        line-height: normal;
      }
    }
  }
}
</style>
